import classnames from 'classnames';
import { useIntl } from 'react-intl';

import { Drawer } from 'ui-components/components/drawer/drawer';
import { FlexRow } from 'ui-components/components/flex';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';

import { TRANSFER_ORDER_ACTION_RECEIVE_SAVED, INVENTORY_UOM_TO_RECEIVED_WITH_UOM } from '../../../../constants/analytics-events';
import { useSendMetrics } from '../../../../hooks/use-send-metrics';
import { messages } from './messages';
import { ReceiveTransferOrderFooter } from './receive-transfer-order-footer';
import { ReceiveTransferOrderForm } from './receive-transfer-order-form';
import styles from './receive-transfer-order-drawer.styles.module.less';
import { TransferOrderDrawerHeader } from './transfer-order-drawer-header';
import { useTransferOrderReceivedProducts } from './transfer-order-receiving-context';
import type { WarehouseLocation } from './types';
import type { TransferOrderRow } from '../data/transfer-orders-context.types';

type ReceiveTransferOrderDrawerProps = {
  onClose: () => void;
  onSuccess?: () => void;
  transferOrder: TransferOrderRow;
  warehouseLocations: WarehouseLocation[];
};

export const ReceiveTransferOrderDrawer = ({ onClose, onSuccess, transferOrder, warehouseLocations }: ReceiveTransferOrderDrawerProps) => {
  const intl = useIntl();
  const { handleSubmitData, isFormTouched, isSaving, receiveFormData } = useTransferOrderReceivedProducts();
  const trackMetrics = useSendMetrics();

  const handleSave = async () => {
    const result = await handleSubmitData();
    if (result) {
      trackMetrics(TRANSFER_ORDER_ACTION_RECEIVE_SAVED, { transferOrderId: transferOrder.transferOrderId });
      const touchedRows = receiveFormData.filter((r) => r.accept || r.reject);
      const uomRows = touchedRows.filter((r) => !!r.unitOfMeasureId && r.unitOfMeasureId !== 0);
      if (uomRows.length > 0) {
        trackMetrics(INVENTORY_UOM_TO_RECEIVED_WITH_UOM, {
          transferOrderId: transferOrder.transferOrderId,
          productsWithUomCount: uomRows.length,
          totalProductsCount: touchedRows.length,
          uomTypeNames: [
            ...new Set(
              uomRows
                .map((r) => r.unitsOfMeasure?.find((u) => u.productUnitOfMeasureId === r.unitOfMeasureId)?.uomTypeName)
                .filter(Boolean),
            ),
          ],
        });
      }
      onSuccess?.();
      onClose();
    }
  };

  return (
    <Drawer
      className={classnames(styles['transfer-order-drawer'])}
      dataTestId='transfer-order-receive-drawer'
      onCancel={onClose}
      onClose={onClose}
    >
      <TransferOrderDrawerHeader
        closeButtonAriaLabel={messages.closeDrawerButtonAriaLabel}
        onClose={onClose}
        title={intl.formatMessage(messages.receiveStock)}
      />

      {isSaving ? (
        <LoadingIndicator />
      ) : (
        <div className={styles['drawer-body']} data-testid='transfer-order-receive-details'>
          <ReceiveTransferOrderForm transferOrder={transferOrder} warehouseLocations={warehouseLocations} />
        </div>
      )}

      <FlexRow className={styles['drawer-footer']} data-testid='transfer-order-receive-footer'>
        <ReceiveTransferOrderFooter disabled={isSaving || !isFormTouched} onClose={onClose} onSave={handleSave} />
      </FlexRow>
    </Drawer>
  );
};
